import {UIAjaxWrapper} from '../../../shared/scripts/network/UIAjaxWrapper'
import {UIAPIEndpoints} from '../../../shared/scripts/network/UIAPIEndpoints'
import {ClientUserState} from '../../../shared/scripts/ClientUserState'
import {DiscordSnowflake} from '@colonist/types'
// eslint-disable-next-line max-len
import {AnalyticEventCategory, AnalyticEventType, BotShareActivityLinkAnalytics, ReferralTypeEnum, ReactivationTypeEnum, RedeemFreeAvatarAnalytics, ShareActivityLinkAnalytics} from '../../../../shared/entity_datas/EntityDatas'
import {AjaxDataTypes} from '../../../../shared/AjaxDataTypes'
import {CA} from '../../../shared/scripts/analytics/Analytics'
import {CustomLinkUtils} from '../discord/utils/CustomLinkUtils'
import {ViewPopupRedeemFreeAvatar} from '../ui_popup/ViewPopupRedeemFreeAvatar'
import {ViewPopupRewardsProgram} from '../ui_popup/ViewPopupRewardsProgram'

export class UIDReferralController {
    private removeChangeListener: () => void
    private readonly referrerId: DiscordSnowflake | string
    private readonly referralOrReactivationType: unknown

    constructor(referrerId: DiscordSnowflake | string, referralOrReactivationType: unknown) {
        this.referrerId = referrerId
        this.referralOrReactivationType = referralOrReactivationType
    }

    async initReferralTracking() {
        if(this.referrerId == undefined) return
        if(this.referralOrReactivationType == undefined) return

        if(this.referrerId == 'bot') {
            const reactivationType = Number(this.referralOrReactivationType) as ReactivationTypeEnum

            this.sendBotReactivationAnalyticsAndTriggerAction(reactivationType)
            return
        }

        const customLink = this.referralOrReactivationType as string

        const referralType = CustomLinkUtils.extractReferralTypeFromCustomLink(customLink) as unknown as ReferralTypeEnum

        void UIDReferralController.sendReferrerValues(this.referrerId, referralType)
    }

    private static async sendReferrerValues(referrerId: DiscordSnowflake, referralTypeId: ReferralTypeEnum) {
        const dataToSend = {referredByUserId: referrerId, referralTypeId: referralTypeId} as unknown as AjaxDataTypes.ReferrerData
        await UIAjaxWrapper.postRequestWhenReady(UIAPIEndpoints.apiSetUserReferrer(), undefined, dataToSend)
    }

    private sendBotReactivationAnalyticsAndTriggerAction(reactivationType: ReactivationTypeEnum) {
        this.removeChangeListener = ClientUserState.registerChangeListener(() => {
            // Defer execution to next tick to ensure assignment to this.removeChangeListener is complete
            setTimeout(async() => {
                if(ClientUserState.haveAbTestsLoaded == false) return

                this.removeChangeListener()

                const abTestReactivateDiscordInactiveUsers = ClientUserState.abTests.REACTIVATE_DISCORD_INACTIVE_USERS

                if(abTestReactivateDiscordInactiveUsers != undefined) {
                    const botShareActivityLinkAnalytics = UIDReferralController.getBotShareActivityLinkAnalytics(reactivationType, abTestReactivateDiscordInactiveUsers)
                    if(botShareActivityLinkAnalytics != undefined) {
                        const eventDataBotShareActivityLink: AjaxDataTypes.AnalyticData = {
                            type: AnalyticEventType.ReactivationAnalytics,
                            category: AnalyticEventCategory.BotShareActivityLink,
                            floatValue: botShareActivityLinkAnalytics,
                        }
                        void CA.sendAnalyticEvent(eventDataBotShareActivityLink)
                    }
                }

                await UIDReferralController.triggerSpecialAction(reactivationType, abTestReactivateDiscordInactiveUsers)
            }, 0)
        })
    }

    private static getBotShareActivityLinkAnalytics(reactivationType: ReactivationTypeEnum, abTest: string | undefined): BotShareActivityLinkAnalytics | undefined {
        switch(reactivationType) {
            case ReactivationTypeEnum.BotDirectMessage24HoursAfterLastActivityLaunch:
                return BotShareActivityLinkAnalytics.BotDirectMessage24HoursAfterLastActivityLaunch
            case ReactivationTypeEnum.BotDirectMessage1WeekAfterLastActivityLaunch:
                return BotShareActivityLinkAnalytics.BotDirectMessage1WeekAfterLastActivityLaunch
            case ReactivationTypeEnum.BotDirectMessage1MonthAfterLastActivityLaunch:
                return BotShareActivityLinkAnalytics.BotDirectMessage1MonthAfterLastActivityLaunch
            case ReactivationTypeEnum.BotDirectMessage3MonthsAfterLastActivityLaunch:
                return BotShareActivityLinkAnalytics.BotDirectMessage3MonthsAfterLastActivityLaunch
            case ReactivationTypeEnum.BotDirectMessage6MonthsAfterLastActivityLaunch:
                return BotShareActivityLinkAnalytics.BotDirectMessage6MonthsAfterLastActivityLaunch
            case ReactivationTypeEnum.BotDirectMessage1YearAfterLastActivityLaunch:
                return BotShareActivityLinkAnalytics.BotDirectMessage1YearAfterLastActivityLaunch
            case ReactivationTypeEnum.BotDirectMessage2YearsAfterLastActivityLaunch:
                return BotShareActivityLinkAnalytics.BotDirectMessage2YearsAfterLastActivityLaunch
            case ReactivationTypeEnum.ABTestStart:
                if(abTest == 'GROUP_A_YOU_HAVE_NOT_PLAYED_FOR_A_WHILE') return BotShareActivityLinkAnalytics.ABTestGroupAStart
                if(abTest == 'GROUP_B_PROMOTE_REFERRAL_PROGRAM') return BotShareActivityLinkAnalytics.ABTestGroupBStart
                if(abTest == 'GROUP_C_GET_FREE_AVATAR') return BotShareActivityLinkAnalytics.ABTestGroupCStart
                return undefined
            case ReactivationTypeEnum.ABTestNow:
                if(abTest == 'GROUP_A_YOU_HAVE_NOT_PLAYED_FOR_A_WHILE') return BotShareActivityLinkAnalytics.ABTestGroupANow
                if(abTest == 'GROUP_B_PROMOTE_REFERRAL_PROGRAM') return BotShareActivityLinkAnalytics.ABTestGroupBNow
                if(abTest == 'GROUP_C_GET_FREE_AVATAR') return BotShareActivityLinkAnalytics.ABTestGroupCNow
                return undefined
            case ReactivationTypeEnum.ABTest2D:
                if(abTest == 'GROUP_A_YOU_HAVE_NOT_PLAYED_FOR_A_WHILE') return BotShareActivityLinkAnalytics.ABTestGroupA2D
                if(abTest == 'GROUP_B_PROMOTE_REFERRAL_PROGRAM') return BotShareActivityLinkAnalytics.ABTestGroupB2D
                if(abTest == 'GROUP_C_GET_FREE_AVATAR') return BotShareActivityLinkAnalytics.ABTestGroupC2D
                return undefined
            case ReactivationTypeEnum.ABTest4D:
                if(abTest == 'GROUP_A_YOU_HAVE_NOT_PLAYED_FOR_A_WHILE') return BotShareActivityLinkAnalytics.ABTestGroupA4D
                if(abTest == 'GROUP_B_PROMOTE_REFERRAL_PROGRAM') return BotShareActivityLinkAnalytics.ABTestGroupB4D
                if(abTest == 'GROUP_C_GET_FREE_AVATAR') return BotShareActivityLinkAnalytics.ABTestGroupC4D
                return undefined
            case ReactivationTypeEnum.ABTest7D:
                if(abTest == 'GROUP_A_YOU_HAVE_NOT_PLAYED_FOR_A_WHILE') return BotShareActivityLinkAnalytics.ABTestGroupA7D
                if(abTest == 'GROUP_B_PROMOTE_REFERRAL_PROGRAM') return BotShareActivityLinkAnalytics.ABTestGroupB7D
                if(abTest == 'GROUP_C_GET_FREE_AVATAR') return BotShareActivityLinkAnalytics.ABTestGroupC7D
                return undefined
            case ReactivationTypeEnum.ABTest15D:
                if(abTest == 'GROUP_A_YOU_HAVE_NOT_PLAYED_FOR_A_WHILE') return BotShareActivityLinkAnalytics.ABTestGroupA15D
                if(abTest == 'GROUP_B_PROMOTE_REFERRAL_PROGRAM') return BotShareActivityLinkAnalytics.ABTestGroupB15D
                if(abTest == 'GROUP_C_GET_FREE_AVATAR') return BotShareActivityLinkAnalytics.ABTestGroupC15D
                return undefined
            default:
                return undefined
        }
    }

    private static async triggerSpecialAction(reactivationType: ReactivationTypeEnum, abTest: string | undefined) {
        if([ReactivationTypeEnum.ABTestStart, ReactivationTypeEnum.ABTestNow, ReactivationTypeEnum.ABTest2D, ReactivationTypeEnum.ABTest4D, ReactivationTypeEnum.ABTest7D, ReactivationTypeEnum.ABTest15D].includes(reactivationType)) {
            switch(abTest) {
                case 'GROUP_A_YOU_HAVE_NOT_PLAYED_FOR_A_WHILE':
                    break
                case 'GROUP_B_PROMOTE_REFERRAL_PROGRAM':
                    new ViewPopupRewardsProgram(ShareActivityLinkAnalytics.ABTestReactivateUserBaseReferralProgramPopupTriggered, ShareActivityLinkAnalytics.ABTestReactivateUserBaseReferralProgramSuccessfullyShared)

                    const eventDataRewardsProgram: AjaxDataTypes.AnalyticData = {
                        type: AnalyticEventType.ReferralAnalytics,
                        category: AnalyticEventCategory.ShareActivityLink,
                        floatValue: ShareActivityLinkAnalytics.ABTestReactivateUserBaseReferralProgramPrePopupTriggered,
                    }
                    void CA.sendAnalyticEvent(eventDataRewardsProgram)
                    break
                case 'GROUP_C_GET_FREE_AVATAR':
                    new ViewPopupRedeemFreeAvatar()

                    const eventDataRedeemFreeAvatar: AjaxDataTypes.AnalyticData = {
                        type: AnalyticEventType.ReactivationAnalytics,
                        category: AnalyticEventCategory.ABTestRedeemFreeAvatar,
                        floatValue: RedeemFreeAvatarAnalytics.ABTestRedeemFreeAvatarPopupTriggered,
                    }
                    void CA.sendAnalyticEvent(eventDataRedeemFreeAvatar)
                    break
                default: break
            }
        }
    }
}
